import { useState, useCallback } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useSearch } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusFilter, type StatusOption } from "@/components/status-filter";
import { CreateProductDialog, IssueCardDialog } from "@/components/dialogs";
import { useAuth } from "@/lib/auth-context";
import { useToast } from "@/hooks/use-toast";
import { DetailCard, DetailGrid, DetailItem } from "@/components/detail-page";
import { ActivityTab } from "@/components/activity-tab";
import { CardsList } from "@/components/inline-entity-list";
import { EntityDrawer, useDrawerEntity, useDrawerSelect } from "@/components/entity-drawer";
import { cn } from "@/lib/utils";
import { Plus, Package, Search, Check, X, Pencil, Trash2, Loader2, RefreshCw, Bot } from "lucide-react";

interface Product {
  id: string;
  name: string;
  description: string | null;
  cardType: string;
  cardNetwork: string;
  currency: string;
  defaultDailyLimitCents: number | null;
  defaultMonthlyLimitCents: number | null;
  defaultSingleTxnLimitCents: number | null;
  maxBalanceCents: number | null;
  minLoadAmountCents: number | null;
  maxLoadAmountCents: number | null;
  bin: string | null;
  binRange: string | null;
  cardValidityYears: number | null;
  kycType: string | null;
  category: string | null;
  velocityLimits: Record<string, any> | null;
  iserveuSyncedAt: string | null;
  status: string;
  requestStatus: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

function getStatusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    ACTIVE: "default",
    INACTIVE: "secondary",
    DRAFT: "outline",
  };
  const colors: Record<string, string> = {
    ACTIVE: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  };
  return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status}</Badge>;
}

function getRequestStatusBadge(status: string) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    APPROVED: "default",
    PENDING: "outline",
    DRAFT: "secondary",
    REJECTED: "destructive",
  };
  const colors: Record<string, string> = {
    APPROVED: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    PENDING: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
  };
  return <Badge variant={variants[status] || "secondary"} className={colors[status]}>{status}</Badge>;
}

const REQUEST_STATUS_OPTIONS: StatusOption[] = [
  { value: "APPROVED", label: "Approved" },
  { value: "PENDING", label: "Pending" },
  { value: "DRAFT", label: "Draft" },
  { value: "REJECTED", label: "Rejected" },
];

// ─── Product Detail Content (drawer) ─────────────────────────────────────────

function ProductDetailContent({ productId, onClose }: { productId: string; onClose: () => void }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { formatCurrency } = useOrgSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Product>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [issueCardOpen, setIssueCardOpen] = useState(false);
  const [portalDataOpen, setPortalDataOpen] = useState(false);

  const { data: product, isLoading } = useQuery<Product>({
    queryKey: ["product", productId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/products/${productId}`);
      if (!response.ok) throw new Error("Failed to fetch product");
      return response.json();
    },
    enabled: !!productId,
  });

  const { data: cards } = useQuery<{ id: string }[]>({
    queryKey: ["product-cards", productId],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/virtual-cards?productId=${productId}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!productId,
  });

  // iServeU portal product details (automation)
  const { data: portalData, isLoading: portalLoading, refetch: refetchPortal, isFetching: portalFetching } = useQuery<{
    status: string;
    data: any;
    cached?: boolean;
  }>({
    queryKey: ["automation-product-details", product?.name],
    queryFn: async () => {
      const params = product?.name ? `?product_name=${encodeURIComponent(product.name)}` : "";
      const response = await apiRequest("GET", `/api/admin/automation/product-details${params}`);
      if (!response.ok) throw new Error("Failed to fetch portal data");
      return response.json();
    },
    enabled: false, // Only fetch when user clicks
    retry: false,
    staleTime: 24 * 60 * 60 * 1000, // 24h
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Product>) => {
      const response = await apiRequest("PATCH", `/api/products/${productId}`, updates);
      if (!response.ok) throw new Error("Failed to update product");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product", productId] });
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setIsEditing(false);
      toast({ title: "Success", description: "Product updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update product", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("DELETE", `/api/products/${productId}`);
      if (!response.ok) throw new Error("Failed to delete product");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({ title: "Success", description: "Product deleted successfully" });
      onClose();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to delete product", variant: "destructive" });
    },
  });

  const handleEdit = () => {
    if (product) {
      setEditForm({
        name: product.name,
        description: product.description || "",
        cardType: product.cardType,
        cardNetwork: product.cardNetwork,
        status: product.status,
        defaultDailyLimitCents: product.defaultDailyLimitCents,
        defaultMonthlyLimitCents: product.defaultMonthlyLimitCents,
        defaultSingleTxnLimitCents: product.defaultSingleTxnLimitCents,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => updateMutation.mutate(editForm);
  const handleCancelEdit = () => { setIsEditing(false); setEditForm({}); };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5 text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Product</span>
        </div>
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-xl font-semibold">{product.name}</h2>
            {getStatusBadge(product.status)}
          </div>
          {product.description && (
            <p className="text-sm text-muted-foreground">{product.description}</p>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={() => setIssueCardOpen(true)}>
                Issue Card
              </Button>
              <Button variant="outline" size="sm" onClick={handleEdit}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteDialogOpen(true)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                Delete
              </Button>
            </>
          )}
        </div>

        <div className="border-t" />

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Product Name</Label>
                <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Card Type</Label>
                <Select value={editForm.cardType} onValueChange={(value) => setEditForm({ ...editForm, cardType: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VIRTUAL">Virtual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Card Network</Label>
                <Select value={editForm.cardNetwork} onValueChange={(value) => setEditForm({ ...editForm, cardNetwork: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visa">Visa</SelectItem>
                    <SelectItem value="mastercard">Mastercard</SelectItem>
                    <SelectItem value="rupay">RuPay</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                    <SelectItem value="DRAFT">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Single Txn Limit (cents)</Label>
                <Input type="number" value={editForm.defaultSingleTxnLimitCents || ""} onChange={(e) => setEditForm({ ...editForm, defaultSingleTxnLimitCents: parseInt(e.target.value) || null })} />
              </div>
              <div className="space-y-2">
                <Label>Daily Limit (cents)</Label>
                <Input type="number" value={editForm.defaultDailyLimitCents || ""} onChange={(e) => setEditForm({ ...editForm, defaultDailyLimitCents: parseInt(e.target.value) || null })} />
              </div>
              <div className="space-y-2">
                <Label>Monthly Limit (cents)</Label>
                <Input type="number" value={editForm.defaultMonthlyLimitCents || ""} onChange={(e) => setEditForm({ ...editForm, defaultMonthlyLimitCents: parseInt(e.target.value) || null })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="cards">Cards ({cards?.length || 0})</TabsTrigger>
              <TabsTrigger value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4">
              <DetailCard title="Basic Information">
                <DetailGrid columns={1}>
                  <DetailItem label="Product Name" value={product.name} />
                  <DetailItem label="Currency" value={product.currency || "—"} />
                  {product.category && <DetailItem label="Category" value={<Badge variant="secondary">{product.category}</Badge>} />}
                  <DetailItem label="Description" value={product.description || "—"} />
                  {product.iserveuSyncedAt && (
                    <DetailItem label="Last Synced from iServeU" value={new Date(product.iserveuSyncedAt).toLocaleString()} />
                  )}
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Card Configuration">
                <DetailGrid columns={1}>
                  <DetailItem label="Card Type" value={<Badge variant="outline">{product.cardType}</Badge>} />
                  <DetailItem label="Card Network" value={<Badge variant="secondary">{product.cardNetwork.toUpperCase()}</Badge>} />
                  {product.bin && <DetailItem label="BIN" value={<code className="text-xs bg-muted px-2 py-1 rounded">{product.bin}</code>} />}
                  {product.binRange && <DetailItem label="BIN Range" value={product.binRange} />}
                  {product.cardValidityYears && <DetailItem label="Card Validity" value={`${product.cardValidityYears} years`} />}
                  {product.kycType && <DetailItem label="KYC Type" value={<Badge variant="outline">{product.kycType}</Badge>} />}
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Default Spending Limits">
                <DetailGrid columns={1}>
                  <DetailItem label="Single Transaction" value={formatCurrency(product.defaultSingleTxnLimitCents)} />
                  <DetailItem label="Daily Limit" value={formatCurrency(product.defaultDailyLimitCents)} />
                  <DetailItem label="Monthly Limit" value={formatCurrency(product.defaultMonthlyLimitCents)} />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Load Limits">
                <DetailGrid columns={1}>
                  <DetailItem label="Minimum Load" value={formatCurrency(product.minLoadAmountCents)} />
                  <DetailItem label="Maximum Load" value={formatCurrency(product.maxLoadAmountCents)} />
                </DetailGrid>
              </DetailCard>

              <DetailCard title="Status">
                <DetailGrid columns={1}>
                  <DetailItem label="Status" value={getStatusBadge(product.status)} />
                  <DetailItem label="Request Status" value={getRequestStatusBadge(product.requestStatus)} />
                </DetailGrid>
              </DetailCard>

              {/* Velocity Limits (from iServeU sync) */}
              {product.velocityLimits && Object.keys(product.velocityLimits).length > 0 && (
                <DetailCard title="Velocity Limits">
                  <div className="space-y-3">
                    {Object.entries(product.velocityLimits).map(([channel, kycTiers]) => (
                      <div key={channel} className="space-y-1">
                        <p className="text-xs font-semibold text-foreground">{channel.replace(/_/g, " ")}</p>
                        {Object.entries(kycTiers as Record<string, any>).map(([kycTier, periods]) => (
                          <div key={kycTier} className="ml-3 space-y-0.5">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kycTier.replace(/_/g, " ")}</p>
                            <div className="flex flex-wrap gap-2 ml-1">
                              {Object.entries(periods as Record<string, any>).map(([period, limits]: [string, any]) => (
                                <span key={period} className="text-xs text-muted-foreground">
                                  <span className="capitalize">{period}</span>: {limits.count} txns / {formatCurrency((limits.amount || 0) * 100)}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </DetailCard>
              )}

              {/* iServeU Portal Data (via automation, SUPER only) */}
              {user?.role === "SUPER" && <DetailCard title={
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <span>iServeU Portal Data</span>
                    {portalData?.cached && (
                      <Badge variant="outline" className="text-[10px] font-normal">Cached</Badge>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={portalFetching}
                    onClick={() => refetchPortal()}
                  >
                    {portalFetching ? (
                      <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    ) : (
                      <Bot className="h-3 w-3 mr-1.5" />
                    )}
                    {portalData ? "Refresh" : "Fetch from Portal"}
                  </Button>
                </div>
              }>
                {portalFetching && !portalData ? (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mb-2" />
                    <p className="text-xs text-muted-foreground">Fetching from iServeU portal...</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">This may take 1–3 minutes</p>
                  </div>
                ) : portalData?.status === "success" && portalData.data ? (
                  <div className="space-y-3">
                    {/* Show key fields inline */}
                    <DetailGrid columns={1}>
                      {(() => {
                        const details = portalData.data.product_details || portalData.data;
                        const keyFields = [
                          "Product Name", "Product Category", "Card Type", "BIN",
                          "BIN Range", "Card Validity (In Years)", "Kyc Type",
                          "POS Max Limit Per Transaction", "Ecom Max Limit Per Transaction",
                          "Min Load Amount", "Max Load Amount",
                        ];
                        const entries = Object.entries(details as Record<string, string>);
                        const shown = portalDataOpen ? entries : entries.filter(([k]) => keyFields.includes(k));

                        return shown.map(([key, value]) => (
                          <DetailItem key={key} label={key} value={String(value)} />
                        ));
                      })()}
                    </DetailGrid>
                    {portalData.data.product_details && Object.keys(portalData.data.product_details).length > 11 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full text-xs text-muted-foreground"
                        onClick={() => setPortalDataOpen(!portalDataOpen)}
                      >
                        {portalDataOpen
                          ? "Show less"
                          : `Show all ${Object.keys(portalData.data.product_details).length} fields`}
                      </Button>
                    )}
                  </div>
                ) : portalData?.status === "error" ? (
                  <p className="text-xs text-destructive py-2">
                    Failed to fetch portal data. The automation bot may be offline.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground py-2">
                    Click "Fetch from Portal" to retrieve live product configuration from the iServeU portal via browser automation.
                  </p>
                )}
              </DetailCard>}

              <DetailCard title="Record Information">
                <DetailGrid columns={1}>
                  <DetailItem label="Product ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{product.id}</code>} />
                  <DetailItem label="Organization ID" value={<code className="text-xs bg-muted px-2 py-1 rounded break-all">{product.organizationId}</code>} />
                  <DetailItem label="Created" value={new Date(product.createdAt).toLocaleString()} />
                  <DetailItem label="Last Updated" value={new Date(product.updatedAt).toLocaleString()} />
                </DetailGrid>
              </DetailCard>
            </TabsContent>

            <TabsContent value="cards">
              <CardsList productId={product.id} onCreate={() => setIssueCardOpen(true)} />
            </TabsContent>

            <TabsContent value="activity">
              <ActivityTab entityType="PRODUCT" entityId={product.id} createdAt={product.createdAt} updatedAt={product.updatedAt} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{product.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteMutation.mutate()} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <IssueCardDialog open={issueCardOpen} onOpenChange={setIssueCardOpen} productId={product.id} />
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function ProductView() {
  const { user } = useAuth();
  const { formatCurrency } = useOrgSettings();
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { drawerEntity, setDrawerEntity, displayEntity } = useDrawerEntity<string>();
  const canCreate = user?.role === "SUPER" || user?.role === "ORGANIZATION";

  // Support ?select= for cross-entity navigation
  useDrawerSelect(setDrawerEntity);

  // Parse status filter from URL
  const searchParams = new URLSearchParams(searchString);
  const urlStatus = searchParams.get("status");
  const [statusFilter, setStatusFilter] = useState<string | null>(urlStatus);

  const { data: products, isLoading } = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/products");
      if (!response.ok) throw new Error("Failed to fetch products");
      return response.json();
    },
  });

  // Handle status filter change with URL sync
  const handleStatusChange = (status: string | null) => {
    setStatusFilter(status);
    const newParams = new URLSearchParams(searchString);
    if (status) {
      newParams.set("status", status);
    } else {
      newParams.delete("status");
    }
    newParams.delete("select");
    const newSearch = newParams.toString();
    setLocation(newSearch ? `?${newSearch}` : "", { replace: true });
  };

  const handleRowClick = useCallback((productId: string) => {
    setDrawerEntity(prev => prev === productId ? null : productId);
  }, [setDrawerEntity]);

  // Filter products by search and status
  const filteredProducts = products?.filter((p) => {
    const matchesSearch =
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.cardType.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.cardNetwork.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || p.requestStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Count by request status for filter badges
  const statusCounts = products?.reduce(
    (acc, p) => {
      acc[p.requestStatus] = (acc[p.requestStatus] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const statusOptionsWithCounts: StatusOption[] = REQUEST_STATUS_OPTIONS.map((opt) => ({
    ...opt,
    count: statusCounts?.[opt.value] || 0,
  }));

  // Approve/Reject mutations
  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/products/${id}/approve`);
      if (!response.ok) throw new Error("Failed to approve");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({ title: "Success", description: "Product approved" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", `/api/products/${id}/reject`);
      if (!response.ok) throw new Error("Failed to reject");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast({ title: "Success", description: "Product rejected" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const showActions = statusFilter === "DRAFT" || statusFilter === "PENDING";

  return (
    <div className="relative min-h-screen">
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight">Products</h1>
            <p className="text-muted-foreground">Manage card products and programs</p>
          </div>
          {canCreate && (
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Create Product
            </Button>
          )}
        </div>

        {/* Status Filters */}
        <StatusFilter
          value={statusFilter}
          onChange={handleStatusChange}
          options={statusOptionsWithCounts}
        />

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {statusFilter ? `${statusFilter.charAt(0) + statusFilter.slice(1).toLowerCase()} Products` : "All Products"}
                </CardTitle>
                <CardDescription>
                  {filteredProducts?.length ?? 0} product{(filteredProducts?.length ?? 0) !== 1 ? "s" : ""} found
                </CardDescription>
              </div>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : filteredProducts?.length === 0 ? (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">No products</h3>
                <p className="text-muted-foreground">
                  {statusFilter
                    ? `No ${statusFilter.toLowerCase()} products found.`
                    : "Create your first card product."}
                </p>
                {!statusFilter && canCreate && (
                  <Button className="mt-4" onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Product
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Network</TableHead>
                    <TableHead>Daily Limit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Request Status</TableHead>
                    <TableHead>Created</TableHead>
                    {showActions && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts?.map((product) => (
                    <TableRow
                      key={product.id}
                      className={cn(
                        "cursor-pointer hover:bg-muted/50",
                        drawerEntity === product.id && "bg-primary/10 hover:bg-primary/15"
                      )}
                      onClick={() => handleRowClick(product.id)}
                    >
                      <TableCell className="font-medium">{product.name}</TableCell>
                      <TableCell><Badge variant="outline">{product.cardType}</Badge></TableCell>
                      <TableCell><Badge variant="secondary">{product.cardNetwork.toUpperCase()}</Badge></TableCell>
                      <TableCell>{formatCurrency(product.defaultDailyLimitCents)}</TableCell>
                      <TableCell>{getStatusBadge(product.status)}</TableCell>
                      <TableCell>{getRequestStatusBadge(product.requestStatus)}</TableCell>
                      <TableCell>{new Date(product.createdAt).toLocaleDateString()}</TableCell>
                      {showActions && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => approveMutation.mutate(product.id)}
                              disabled={approveMutation.isPending}
                            >
                              <Check className="mr-1 h-4 w-4" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => rejectMutation.mutate(product.id)}
                              disabled={rejectMutation.isPending}
                            >
                              <X className="mr-1 h-4 w-4" />
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <CreateProductDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />
      </div>

      {/* Right-side detail drawer */}
      <EntityDrawer isOpen={!!drawerEntity} onClose={() => setDrawerEntity(null)}>
        {displayEntity && (
          <ProductDetailContent key={displayEntity} productId={displayEntity} onClose={() => setDrawerEntity(null)} />
        )}
      </EntityDrawer>
    </div>
  );
}
